import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import api, { LOGO_URL, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InstallAppButton } from "@/components/InstallAppButton";

export default function AdminLogin() {
  const { login, setSession, user } = useAuth();
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [gatePw, setGatePw] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", password: "" });
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState("phone"); // phone | email
  const [phoneInput, setPhoneInput] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [smsCode, setSmsCode] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");

  useEffect(() => { if (user && (user.role === "admin" || user.role === "worker")) navigate("/admin"); }, [user, navigate]);

  async function unlock(e) {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/verify-gate", { gate_password: gatePw });
      setUnlocked(true);
      toast.success("Access granted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid access password");
    } finally { setLoading(false); }
  }

  async function requestSmsCode(e) {
    e.preventDefault();
    if (!phoneInput.trim()) return toast.error("Enter your phone number.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/staff-phone/request-code", { phone: phoneInput.trim() });
      setMaskedPhone(data.phone_masked); setCodeSent(true);
      toast.success(`Code texted to ${data.phone_masked}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send code."); }
    finally { setLoading(false); }
  }

  async function verifySmsCode(e) {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/staff-phone/verify", { phone: phoneInput.trim(), code: smsCode });
      if (data.user.role !== "admin" && data.user.role !== "worker") { setLoading(false); return toast.error("This account cannot access the staff portal."); }
      setSession(data);
      toast.success(`Welcome back, ${data.user.name?.split(" ")[0] || "team"}`); navigate("/admin");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code."); }
    finally { setLoading(false); }
  }

  function resetPhoneFlow() { setCodeSent(false); setSmsCode(""); setMaskedPhone(""); }

  async function submitLogin(e) {
    e.preventDefault(); setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (!res.ok) return toast.error(res.error);
    if (res.user.role !== "admin" && res.user.role !== "worker") return toast.error("This account cannot access the staff portal.");
    toast.success(`Welcome back, ${res.user.name?.split(" ")[0] || "team"}`); navigate("/admin");
  }

  async function submitForgot(e) {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("If a staff account exists for that email, a reset link is on its way.");
      setForgot(false); setForgotEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally { setLoading(false); }
  }

  async function submitCreate(e) {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/auth/create-admin", { ...reg, gate_password: gatePw });
      setSession(data);
      toast.success("Admin account created"); navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create admin");
    } finally { setLoading(false); }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-accent px-6 grain-bg">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="admin-login-back-website" className="mb-4 inline-flex items-center gap-1 text-sm text-teal-100 transition-colors hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Back to Crystal Pharmacy
        </Link>
        <div className="rounded-3xl border border-border bg-card p-8 shadow-2xl">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={LOGO_URL} alt="Crystal Pharmacy" className="mb-4 h-12 w-auto" />
          <div className="flex items-center gap-2 text-primary">
            <Lock className="h-4 w-4" /><span className="text-sm font-semibold uppercase tracking-[0.2em]">Staff Portal</span>
          </div>
        </div>

        {!unlocked ? (
          <form onSubmit={unlock} className="space-y-4">
            <div className="flex items-center gap-2 rounded-lg bg-secondary p-3 text-xs text-secondary-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" /> Enter the staff access password to continue.
            </div>
            <div className="space-y-2">
              <Label>Access password</Label>
              <Input type="password" data-testid="admin-gate-password" value={gatePw} onChange={(e) => setGatePw(e.target.value)} required autoFocus />
            </div>
            <Button type="submit" disabled={loading} data-testid="admin-gate-submit" className="w-full rounded-full">
              {loading ? "Checking..." : "Unlock"}
            </Button>
            <InstallAppButton />
          </form>
        ) : (
          <Tabs defaultValue="login">
            <TabsList className="mb-6 grid w-full grid-cols-2">
              <TabsTrigger value="login" data-testid="admin-tab-login">Log In</TabsTrigger>
              <TabsTrigger value="create" data-testid="admin-tab-create">Create Admin</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              {forgot ? (
                <form onSubmit={submitForgot} className="space-y-4">
                  <p className="text-sm text-muted-foreground">Enter your staff email and we'll send you a link to reset your password.</p>
                  <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="admin-forgot-email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required autoFocus /></div>
                  <Button type="submit" disabled={loading} data-testid="admin-forgot-submit" className="w-full rounded-full">{loading ? "Sending..." : "Send Reset Link"}</Button>
                  <button type="button" onClick={() => setForgot(false)} data-testid="admin-forgot-back" className="w-full text-center text-sm text-muted-foreground hover:text-primary">Back to sign in</button>
                </form>
              ) : loginMode === "phone" ? (
                !codeSent ? (
                  <form onSubmit={requestSmsCode} className="space-y-4">
                    <p className="text-sm text-muted-foreground">Enter your phone number and we'll text you a one-time login code.</p>
                    <div className="space-y-2">
                      <Label>Your phone number</Label>
                      <Input type="tel" inputMode="tel" data-testid="phone-login-input" placeholder="(505) 555-0199" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} autoFocus />
                    </div>
                    <Button type="submit" disabled={loading || !phoneInput.trim()} data-testid="phone-login-request" className="w-full rounded-full">{loading ? "Sending..." : "Text me a code"}</Button>
                    <button type="button" onClick={() => setLoginMode("email")} data-testid="use-email-login" className="w-full text-center text-sm text-muted-foreground hover:text-primary">Log in with email &amp; password instead</button>
                  </form>
                ) : (
                  <form onSubmit={verifySmsCode} className="space-y-4">
                    <p className="text-sm text-muted-foreground">Enter the 6-digit code we texted to <strong>{maskedPhone}</strong>.</p>
                    <div className="space-y-2">
                      <Label>Login code</Label>
                      <Input inputMode="numeric" maxLength={6} data-testid="phone-login-code" value={smsCode} onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ""))} required autoFocus />
                    </div>
                    <Button type="submit" disabled={loading || smsCode.length < 4} data-testid="phone-login-verify" className="w-full rounded-full">{loading ? "Verifying..." : "Verify & sign in"}</Button>
                    <div className="flex items-center justify-between text-sm">
                      <button type="button" onClick={resetPhoneFlow} data-testid="phone-login-change" className="text-muted-foreground hover:text-primary">Use a different number</button>
                      <button type="button" onClick={requestSmsCode} data-testid="phone-login-resend" className="text-primary hover:underline">Resend code</button>
                    </div>
                  </form>
                )
              ) : (
                <form onSubmit={submitLogin} className="space-y-4">
                  <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="admin-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
                  <div className="space-y-2"><Label>Password</Label><Input type="password" data-testid="admin-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required /></div>
                  <Button type="submit" disabled={loading} data-testid="admin-login-submit" className="w-full rounded-full">{loading ? "Signing in..." : "Sign In"}</Button>
                  <div className="flex items-center justify-between text-sm">
                    <button type="button" onClick={() => { setLoginMode("phone"); resetPhoneFlow(); }} data-testid="use-phone-login" className="text-muted-foreground hover:text-primary">Use phone code instead</button>
                    <button type="button" onClick={() => setForgot(true)} data-testid="admin-forgot-link" className="text-primary hover:underline">Forgot password?</button>
                  </div>
                </form>
              )}
            </TabsContent>
            <TabsContent value="create">
              <form onSubmit={submitCreate} className="space-y-4">
                <div className="space-y-2"><Label>Full name</Label><Input data-testid="admin-create-name" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} required /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="admin-create-email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} required /></div>
                <div className="space-y-2"><Label>Password</Label><Input type="password" data-testid="admin-create-password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} required /></div>
                <Button type="submit" disabled={loading} data-testid="admin-create-submit" className="w-full rounded-full">{loading ? "Creating..." : "Create Admin Account"}</Button>
              </form>
            </TabsContent>
          </Tabs>
        )}
        </div>
      </div>
    </div>
  );
}
